/**
 * An issuer the unified login trusts. A callback is redeemed only at the issuer its RFC 9207 `iss`
 * names, and only if that issuer is on this list.
 */
export interface IdPIssuerConfig {
  /**
   * The exact `iss` value, with its trailing slash (`https://login.twilio.com/`). Discovery is
   * read from `<issuer>.well-known/openid-configuration`.
   */
  issuer: string;
  clientId: string;
  /**
   * Overrides discovery's `authorization_endpoint`. Only the first issuer's is used, because
   * every login starts there: the Twilio proxy picks the IdP and the callback's `iss` says which.
   */
  authorizationEndpoint?: string;
  /**
   * With `authorizationEndpoint`, makes the metadata static: no discovery request is made.
   */
  tokenEndpoint?: string;
  /**
   * Overrides discovery's `end_session_endpoint` (Auth0's discovery has none).
   */
  endSessionEndpoint?: string;
  /**
   * The query parameter the end-session endpoint reads the return URL from. Auth0's `/v2/logout`
   * reads `returnTo`; OIDC RP-initiated logout reads `post_logout_redirect_uri`.
   */
  logoutReturnParam: "returnTo" | "post_logout_redirect_uri";
}

const withScheme = (host: string) => (/^https?:\/\//.test(host) ? host : `https://${host}`);
const withoutTrailingSlash = (url: string) => url.replace(/\/+$/, "");

/**
 * The Auth0 tenant, from the same `VITE_DOMAIN` / `VITE_PROXY_DOMAIN_FOR_AUTHORIZE` values the
 * Auth0 SDK read. A bare host means `https://`.
 */
export function auth0Issuer(opts: {
  domain: string;
  clientId: string;
  proxyDomainForAuthorize?: string;
}): IdPIssuerConfig {
  const base = withoutTrailingSlash(withScheme(opts.domain));
  const authorizeBase = opts.proxyDomainForAuthorize
    ? withoutTrailingSlash(withScheme(opts.proxyDomainForAuthorize))
    : base;
  return {
    issuer: `${base}/`,
    clientId: opts.clientId,
    authorizationEndpoint: `${authorizeBase}/authorize`,
    tokenEndpoint: `${base}/oauth/token`,
    endSessionEndpoint: `${base}/v2/logout`,
    logoutReturnParam: "returnTo",
  };
}

const isLocalhost = (url: URL) => url.hostname === "localhost" || url.hostname === "127.0.0.1";

/** Whether `issuer` is an https URL (http only for localhost) ending in "/". */
const isValidIssuer = (issuer: unknown): issuer is string => {
  if (typeof issuer !== "string" || !issuer.endsWith("/")) {
    return false;
  }
  try {
    const url = new URL(issuer);
    return url.protocol === "https:" || (url.protocol === "http:" && isLocalhost(url));
  } catch {
    return false;
  }
};

/**
 * Parses `VITE_IDP_ADDITIONAL_ISSUERS`: a JSON array of `{ "issuer", "clientId" }`, each a
 * standard OIDC issuer read through discovery. Unset in every deployed environment; the OIDC
 * journeys set it to add the mock's second issuer. Throws on a malformed value, so a bad build
 * fails at start-up.
 */
export function parseAdditionalIssuers(raw: string | undefined): IdPIssuerConfig[] {
  if (!raw?.trim()) {
    return [];
  }
  const parsed: unknown = JSON.parse(raw);
  if (!Array.isArray(parsed)) {
    throw new Error("VITE_IDP_ADDITIONAL_ISSUERS must be a JSON array");
  }
  return parsed.map((entry: unknown) => {
    const { issuer, clientId } = (entry ?? {}) as Record<string, unknown>;
    if (!isValidIssuer(issuer) || typeof clientId !== "string" || !clientId) {
      throw new Error(
        'Each VITE_IDP_ADDITIONAL_ISSUERS entry needs an https "issuer" ending in "/" and a "clientId"'
      );
    }
    return { issuer, clientId, logoutReturnParam: "post_logout_redirect_uri" as const };
  });
}
